import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const TOTAL_SLIDES = 10;

function Onboarding() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(() => {
    const saved = localStorage.getItem('onboarding_slide');
    return saved ? Math.min(parseInt(saved, 10), TOTAL_SLIDES - 1) : 0;
  });
  const [answers, setAnswers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('onboarding_answers')) || {}; } catch { return {}; }
  });
  const touchRef = useRef({ startX: 0, startY: 0 });
  const containerRef = useRef(null);

  useEffect(() => { localStorage.setItem('onboarding_slide', current); }, [current]);
  useEffect(() => { localStorage.setItem('onboarding_answers', JSON.stringify(answers)); }, [answers]);

  const skip = useCallback(() => {
    localStorage.setItem('onboarding_complete', 'true');
    navigate('/landing');
  }, [navigate]);

  const next = useCallback(() => {
    if (current < TOTAL_SLIDES - 1) setCurrent(c => c + 1);
  }, [current]);

  const prev = useCallback(() => {
    if (current > 0) setCurrent(c => c - 1);
  }, [current]);

  const toggleChip = (key, value) => {
    setAnswers(prev => {
      const arr = prev[key] || [];
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
  };

  const selectSingle = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleTouchStart = (e) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx < 0) next(); else prev();
    }
  };

  const finish = () => {
    localStorage.setItem('onboarding_complete', 'true');
    navigate('/register');
  };

  const Chip = ({ label, selected, onClick }) => (
    <button className={`ob-chip ${selected ? 'ob-chip--selected' : ''}`} onClick={onClick} type="button">{label}</button>
  );

  const slides = [
    // 0 - Hero
    <div className="ob-slide-content" key={0}>
      <div className="ob-icon-hero">
        <svg width="256" height="256" viewBox="0 0 64 64" fill="none">
          <defs>
            <radialGradient id="leafFill" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(40 28) rotate(135) scale(28)">
              <stop stopColor="#6E7F3D" stopOpacity="0.16"/>
              <stop offset="1" stopColor="#6E7F3D" stopOpacity="0.06"/>
            </radialGradient>
          </defs>
          <path d="M32 10 C20 14 14 26 18 38 C21 47 27 53 32 54 C37 53 43 47 46 38 C50 26 44 14 32 10Z" fill="url(#leafFill)"/>
          <g stroke="#6E7F3D" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M32 10 C20 14 14 26 18 38 C21 47 27 53 32 54 C37 53 43 47 46 38 C50 26 44 14 32 10Z"/>
            <path d="M32 18v36"/>
            <path d="M32 30c-6-3-10-7-12-12"/>
            <path d="M32 30c6-3 10-7 12-12"/>
            <path d="M32 40c-5-2-8-5-10-9"/>
            <path d="M32 40c5-2 8-5 10-9"/>
          </g>
        </svg>
      </div>
      <h1 className="ob-heading">Finally understand what's upsetting your gut</h1>
      <p className="ob-subtext">Track meals, log symptoms, and discover your triggers — effortlessly.</p>
    </div>,

    // 1 - Empathy
    <div className="ob-slide-content" key={1}>
      <div className="ob-icon-hero">
        <svg width="256" height="256" viewBox="0 0 64 64" fill="none">
          <defs>
            <linearGradient id="softFill" x1="18" y1="42" x2="46" y2="22" gradientUnits="userSpaceOnUse">
              <stop stopColor="#6E7F3D" stopOpacity="0.16"/>
              <stop offset="1" stopColor="#6E7F3D" stopOpacity="0.06"/>
            </linearGradient>
          </defs>
          <path d="M19 39c1.2-3.2 4.2-5.4 7.9-5.4h10.2c3.7 0 6.7 2.2 7.9 5.4v6.6c0 2.2-1.8 4-4 4H23c-2.2 0-4-1.8-4-4V39Z" fill="url(#softFill)"/>
          <path d="M9 40.5c.8-2.3 2.9-3.9 5.4-3.9h5.5c2.5 0 4.6 1.6 5.4 3.9v4.7c0 1.9-1.5 3.4-3.4 3.4h-9.9c-1.9 0-3.4-1.5-3.4-3.4v-4.7Z" fill="#6E7F3D" fillOpacity="0.08"/>
          <path d="M39.7 40.5c.8-2.3 2.9-3.9 5.4-3.9h5.5c2.5 0 4.6 1.6 5.4 3.9v4.7c0 1.9-1.5 3.4-3.4 3.4h-9.9c-1.9 0-3.4-1.5-3.4-3.4v-4.7Z" fill="#6E7F3D" fillOpacity="0.08"/>
          <g stroke="#6E7F3D" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="32" cy="22" r="7"/>
            <circle cx="16.5" cy="26" r="5.5"/>
            <circle cx="47.5" cy="26" r="5.5"/>
            <path d="M19 39c1.2-3.2 4.2-5.4 7.9-5.4h10.2c3.7 0 6.7 2.2 7.9 5.4v6.6c0 2.2-1.8 4-4 4H23c-2.2 0-4-1.8-4-4V39Z"/>
            <path d="M9 40.5c.8-2.3 2.9-3.9 5.4-3.9h5.5c2.5 0 4.6 1.6 5.4 3.9v4.7c0 1.9-1.5 3.4-3.4 3.4h-9.9c-1.9 0-3.4-1.5-3.4-3.4v-4.7Z"/>
            <path d="M39.7 40.5c.8-2.3 2.9-3.9 5.4-3.9h5.5c2.5 0 4.6 1.6 5.4 3.9v4.7c0 1.9-1.5 3.4-3.4 3.4h-9.9c-1.9 0-3.4-1.5-3.4-3.4v-4.7Z"/>
          </g>
          <circle cx="23" cy="43" r="4.3" fill="#E7F0D0" fillOpacity="0.55"/>
          <circle cx="41" cy="43" r="4.3" fill="#E7F0D0" fillOpacity="0.55"/>
        </svg>
      </div>
      <h1 className="ob-heading">You're not alone</h1>
      <p className="ob-subtext ob-big-stat">1 in 7 people struggle with digestive issues every day.</p>
      <p className="ob-subtext">It's frustrating, exhausting, and isolating. We get it — and we're here to help.</p>
    </div>,

    // 2 - What brings you here
    <div className="ob-slide-content" key={2}>
      <h1 className="ob-heading">What brings you here?</h1>
      <p className="ob-subtext">Select all that apply</p>
      <div className="ob-chips">
        {['IBS', 'Crohn\'s Disease', 'Ulcerative Colitis', 'Food Sensitivities', 'Just Exploring'].map(item => (
          <Chip key={item} label={item} selected={(answers.condition || []).includes(item)} onClick={() => toggleChip('condition', item)} />
        ))}
      </div>
    </div>,

    // 3 - Duration
    <div className="ob-slide-content" key={3}>
      <h1 className="ob-heading">How long have you been dealing with this?</h1>
      <div className="ob-options">
        {['Just started', 'A few months', 'A year or more', 'As long as I can remember'].map(item => (
          <button key={item} className={`ob-option ${answers.duration === item ? 'ob-option--selected' : ''}`} onClick={() => selectSingle('duration', item)} type="button">{item}</button>
        ))}
      </div>
    </div>,

    // 4 - Triggers
    <div className="ob-slide-content" key={4}>
      <h1 className="ob-heading">Have you found any triggers yet?</h1>
      <p className="ob-subtext">Select all that apply</p>
      <div className="ob-chips">
        {['Dairy', 'Gluten', 'Spicy Food', 'FODMAPs', 'Caffeine', 'Alcohol', 'Not sure yet'].map(item => (
          <Chip key={item} label={item} selected={(answers.triggers || []).includes(item)} onClick={() => toggleChip('triggers', item)} />
        ))}
      </div>
    </div>,

    // 5 - Snap a meal
    <div className="ob-slide-content" key={5}>
      <h1 className="ob-heading">Snap a meal, we handle the rest</h1>
      <p className="ob-subtext">Our AI identifies every ingredient — even the hidden ones.</p>
      <div className="ob-phone-mock">
        <div className="ob-phone-notch"></div>
        <div className="ob-phone-screen">
          <div className="ob-meal-result">
            <div className="ob-meal-photo">🍝</div>
            <div className="ob-meal-name">Chicken Alfredo Pasta</div>
            <div className="ob-meal-ingredients">
              <span className="ob-ingredient ob-ing-warn">cream</span>
              <span className="ob-ingredient ob-ing-warn">parmesan</span>
              <span className="ob-ingredient ob-ing-warn">garlic</span>
              <span className="ob-ingredient">chicken</span>
              <span className="ob-ingredient">fettuccine</span>
              <span className="ob-ingredient ob-ing-warn">butter</span>
              <span className="ob-ingredient ob-ing-warn">gluten</span>
            </div>
            <div className="ob-meal-note">⚠️ Contains dairy, gluten, and alliums</div>
          </div>
        </div>
      </div>
    </div>,

    // 6 - Track everything
    <div className="ob-slide-content" key={6}>
      <h1 className="ob-heading">See your full picture</h1>
      <p className="ob-subtext">Meals, bowel movements, stress, sleep — all in one timeline.</p>
      <div className="ob-phone-mock">
        <div className="ob-phone-notch"></div>
        <div className="ob-phone-screen">
          <div className="ob-history-mock">
            <div className="ob-history-date">Today</div>
            <div className="ob-history-item">
              <span className="ob-history-icon">😰</span>
              <div className="ob-history-detail">
                <div className="ob-history-title">Daily Check-in</div>
                <div className="ob-history-pills">
                  <span className="ob-pill">Stress: High</span>
                  <span className="ob-pill">Sleep: Poor</span>
                </div>
              </div>
            </div>
            <div className="ob-history-item">
              <span className="ob-history-icon">🍽️</span>
              <div className="ob-history-detail">
                <div className="ob-history-title">Chicken Alfredo</div>
                <div className="ob-history-sub">12:30 PM · cream, garlic, gluten</div>
              </div>
            </div>
            <div className="ob-history-item">
              <span className="ob-history-icon">💩</span>
              <div className="ob-history-detail">
                <div className="ob-history-title">Bristol Type 6</div>
                <div className="ob-history-sub">3:15 PM · Bloating, Cramping</div>
              </div>
            </div>
            <div className="ob-history-item">
              <span className="ob-history-icon">🍽️</span>
              <div className="ob-history-detail">
                <div className="ob-history-title">Grilled Salmon & Rice</div>
                <div className="ob-history-sub">6:00 PM · salmon, rice, lemon</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,

    // 7 - Discover triggers
    <div className="ob-slide-content" key={7}>
      <h1 className="ob-heading">Discover what's causing your symptoms</h1>
      <p className="ob-subtext">AI-powered analysis finds patterns you'd never spot on your own.</p>
      <div className="ob-phone-mock">
        <div className="ob-phone-notch"></div>
        <div className="ob-phone-screen">
          <div className="ob-insights-mock">
            <div className="ob-insight-section">
              <div className="ob-insight-label">🔴 Likely Triggers</div>
              <div className="ob-insight-row ob-insight-bad">
                <span>Dairy</span>
                <span className="ob-insight-bar"><span className="ob-bar-fill ob-bar-red" style={{width:'85%'}}></span></span>
                <span className="ob-insight-pct">85%</span>
              </div>
              <div className="ob-insight-row ob-insight-bad">
                <span>Garlic</span>
                <span className="ob-insight-bar"><span className="ob-bar-fill ob-bar-red" style={{width:'68%'}}></span></span>
                <span className="ob-insight-pct">68%</span>
              </div>
            </div>
            <div className="ob-insight-section">
              <div className="ob-insight-label">🟢 Safe Foods</div>
              <div className="ob-insight-row ob-insight-good">
                <span>Rice</span>
                <span className="ob-insight-bar"><span className="ob-bar-fill ob-bar-green" style={{width:'95%'}}></span></span>
                <span className="ob-insight-pct">Safe</span>
              </div>
              <div className="ob-insight-row ob-insight-good">
                <span>Chicken</span>
                <span className="ob-insight-bar"><span className="ob-bar-fill ob-bar-green" style={{width:'90%'}}></span></span>
                <span className="ob-insight-pct">Safe</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,

    // 8 - Motivation
    <div className="ob-slide-content" key={8}>
      <h1 className="ob-heading">Your first insight in as little as 5 meals</h1>
      <p className="ob-subtext">Most users discover their first trigger within 2 weeks.</p>
      <div className="ob-milestone-path">
        <div className="ob-milestone">
          <div className="ob-milestone-num">5</div>
          <div className="ob-milestone-text">meals logged</div>
        </div>
        <div className="ob-milestone-connector">+</div>
        <div className="ob-milestone">
          <div className="ob-milestone-num">3</div>
          <div className="ob-milestone-text">BMs tracked</div>
        </div>
        <div className="ob-milestone-connector">=</div>
        <div className="ob-milestone ob-milestone-unlock">
          <div className="ob-milestone-num">
            <svg width="36" height="36" viewBox="0 0 64 64" fill="none">
              <g stroke="#6E7F3D" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 30v-6a10 10 0 0 1 20 0v6"/>
                <path d="M20 30h24a4 4 0 0 1 4 4v16a6 6 0 0 1-6 6H22a6 6 0 0 1-6-6V34a4 4 0 0 1 4-4Z"/>
                <circle cx="32" cy="42" r="2.6"/>
                <path d="M32 44.6v5"/>
              </g>
            </svg>
          </div>
          <div className="ob-milestone-text">Insights unlocked</div>
        </div>
      </div>
    </div>,

    // 9 - CTA
    <div className="ob-slide-content" key={9}>
      <div className="ob-icon-hero">
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
          <g stroke="#6E7F3D" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 48h28"/>
            <path d="M32 48V26"/>
            <path d="M32 33c-7 0-12-5-12-12c7 0 12 5 12 12Z"/>
            <path d="M32 36c8 0 14-5.5 14-14c-8 0-14 5.5-14 14Z"/>
          </g>
        </svg>
      </div>
      <h1 className="ob-heading">Ready to start healing?</h1>
      <p className="ob-subtext">Create a free account to save your progress and get personalized insights.</p>
      <button className="btn btn-primary ob-cta-btn" onClick={finish} type="button">Get Started</button>
      <p className="ob-cta-links">
        Already have an account? <span className="ob-link" onClick={() => { localStorage.setItem('onboarding_complete', 'true'); navigate('/login'); }}>Log in</span>
      </p>
      <p className="ob-cta-guest" onClick={() => { localStorage.setItem('onboarding_complete', 'true'); navigate('/login'); }}>I already have an account</p>
    </div>,
  ];

  return (
    <div className="ob-container" ref={containerRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <div className="ob-skip" onClick={skip}>Skip</div>
      <div className="ob-slider" style={{ transform: `translateX(-${current * 100}%)` }}>
        {slides.map((slide, i) => (
          <div className="ob-slide" key={i}>{slide}</div>
        ))}
      </div>
      <div className="ob-bottom">
        {current < TOTAL_SLIDES - 1 && (
          <button className="btn btn-primary ob-next-btn" onClick={next} type="button">
            {current >= 2 && current <= 4 ? 'Continue' : 'Next'}
          </button>
        )}
        <div className="ob-dots">
          {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
            <div key={i} className={`ob-dot ${i <= current ? 'ob-dot--filled' : ''}`} onClick={() => setCurrent(i)} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
